import { getAuthHeaders, getCsrfToken } from '@/app/utils/api/auth-headers';

/**
 * Generic Server-Sent Event payload emitted by the atb backend
 * (deploy / import / client delete share this shape).
 */
export interface SseProgressEvent {
  step: string;
  current: number;
  total: number;
  message: string;
}

interface ParsedSseEvent {
  eventName: string;
  data: unknown;
}

function parseSseEvent(part: string): ParsedSseEvent | null {
  const lines = part.split('\n');
  const eventLine = lines.find((l) => l.startsWith('event:'));
  const dataLine = lines.find((l) => l.startsWith('data:'));
  if (!dataLine) return null;
  return {
    eventName: eventLine ? eventLine.slice(6).trim() : '',
    data: JSON.parse(dataLine.slice(5).trim()) as unknown,
  };
}

interface ProcessedEvents<TComplete> {
  completePayload: TComplete | null;
  reachedComplete: boolean;
}

function extractErrorMessage(data: unknown, fallback: string): string {
  if (
    data !== null &&
    typeof data === 'object' &&
    'message' in data &&
    typeof (data as { message: unknown }).message === 'string'
  ) {
    return (data as { message: string }).message;
  }
  return fallback;
}

function processSseEventChunks<TProgress, TComplete>(
  parts: string[],
  onProgress: ((_event: TProgress) => void) | undefined,
  defaultErrorMessage: string
): ProcessedEvents<TComplete> {
  for (const part of parts) {
    const parsed = parseSseEvent(part);
    if (!parsed) continue;

    if (parsed.eventName === 'error') {
      throw new Error(extractErrorMessage(parsed.data, defaultErrorMessage));
    }
    if (parsed.eventName === 'complete') {
      return { completePayload: parsed.data as TComplete, reachedComplete: true };
    }
    if (onProgress) {
      onProgress(parsed.data as TProgress);
    }
  }
  return { completePayload: null, reachedComplete: false };
}

/**
 * Read an SSE response body to completion.
 *
 * - Calls `onProgress` for every event whose name is not `complete` or `error`.
 * - Throws if an `error` event is received (using the event's `message`).
 * - Returns the data payload of the terminating `complete` event (if any),
 *   or `null` if the stream ended without one.
 */
export async function readSseStream<TProgress = SseProgressEvent, TComplete = TProgress>(
  response: Response,
  onProgress?: (_event: TProgress) => void,
  defaultErrorMessage = 'Operation failed'
): Promise<TComplete | null> {
  if (!response.body) {
    throw new Error(`${defaultErrorMessage}: response body is empty`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    const result = processSseEventChunks<TProgress, TComplete>(
      parts,
      onProgress,
      defaultErrorMessage
    );
    if (result.reachedComplete) {
      return result.completePayload;
    }
  }

  return null;
}

/**
 * POST to an SSE endpoint, attaching the standard auth + CSRF headers used
 * across the app, and validate the response. Caller is responsible for
 * consuming the body via `readSseStream`.
 *
 * - If `body` is a `FormData` instance it is forwarded as-is and the browser
 *   sets the multipart boundary automatically (no `Content-Type` header is
 *   added).
 * - If `body` is `undefined`, no body is sent.
 * - Otherwise the body is JSON-encoded and `Content-Type: application/json`
 *   is added.
 */
export async function postSseRequest(
  url: string,
  body?: unknown,
  defaultErrorMessage = 'Request failed'
): Promise<Response> {
  const headers = getAuthHeaders();
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }

  let fetchBody: BodyInit | undefined;
  if (body === undefined) {
    fetchBody = undefined;
  } else if (body instanceof FormData) {
    fetchBody = body;
    // Let the browser set the multipart boundary automatically. The default
    // 'application/json' from getAuthHeaders() must be removed or the server
    // will fail to parse the multipart payload.
    delete headers['Content-Type'];
  } else {
    fetchBody = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: fetchBody,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `${defaultErrorMessage} with status ${response.status}`
    );
  }

  return response;
}
